import { useState, useEffect } from "react";
import { Download, Search, AlertCircle } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { api } from "../lib/api"; 

export default function RemarksReport() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [meta, setMeta] = useState({ programs: [], branches: [], batches: [], semesters: [] });
  
  const [filters, setFilters] = useState({
    program: "",
    branch: "",
    batch: "",
    semester: ""
  });

  useEffect(() => {
    // Fetch the dynamic dropdown data directly from the uploaded DB records
    api.get("/admin/reports/filters")
      .then(res => setMeta(res.data))
      .catch(console.error);
      
    fetchReport(); 
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchReport = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.program) params.append("program", filters.program);
      if (filters.branch) params.append("branch", filters.branch);
      if (filters.batch) params.append("batch", filters.batch);
      if (filters.semester) params.append("semester", filters.semester);

      const res = await api.get(`/admin/reports/remarks?${params.toString()}`);
      setData(res.data.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const downloadCSV = () => {
    if (data.length === 0) return;
    
    // 1. Group the flat records by Student (Roll No)
    const studentMap = {};
    
    data.forEach(row => {
      const roll = row.roll_no;
      
      // Initialize the student row if they don't exist yet
      if (!studentMap[roll]) {
        studentMap[roll] = {
          roll_no: row.roll_no,
          name: row.name,
          program: row.program,
          branch: row.branch,
          batch: row.batch,
          semesters: {
            "I": "", "II": "", "III": "", "IV": "",
            "V": "", "VI": "", "VII": "", "VIII": ""
          }
        };
      }
      
      // Assign the remark to the specific semester column
      if (row.semester && studentMap[roll].semesters[row.semester] !== undefined) {
        const existingRemark = studentMap[roll].semesters[row.semester];
        const newRemark = row.remark ? row.remark.trim() : "";
        
        // If a student somehow has multiple remark entries for the exact same semester, merge them
        studentMap[roll].semesters[row.semester] = existingRemark 
          ? `${existingRemark} | ${newRemark}` 
          : newRemark;
      }
    });

    // 2. Define the wide-format headers
    const headers = [
      "Roll No", "Name", "Program", "Branch", "Batch", 
      "Sem I", "Sem II", "Sem III", "Sem IV", 
      "Sem V", "Sem VI", "Sem VII", "Sem VIII"
    ];
    
    const csvRows = [headers.join(",")];
    
    // 3. Map the grouped students to CSV rows
    Object.values(studentMap).forEach(student => {
      const values = [
        `"${student.roll_no || ""}"`,
        `"${student.name || ""}"`,
        `"${student.program || ""}"`,
        `"${student.branch || ""}"`,
        `"${student.batch || ""}"`,
        `"${(student.semesters["I"] || "").replace(/"/g, '""')}"`,
        `"${(student.semesters["II"] || "").replace(/"/g, '""')}"`,
        `"${(student.semesters["III"] || "").replace(/"/g, '""')}"`,
        `"${(student.semesters["IV"] || "").replace(/"/g, '""')}"`,
        `"${(student.semesters["V"] || "").replace(/"/g, '""')}"`,
        `"${(student.semesters["VI"] || "").replace(/"/g, '""')}"`,
        `"${(student.semesters["VII"] || "").replace(/"/g, '""')}"`,
        `"${(student.semesters["VIII"] || "").replace(/"/g, '""')}"`
      ];
      csvRows.push(values.join(","));
    });
    
    // 4. Trigger the download
    const blob = new Blob([csvRows.join("\n")], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.setAttribute("hidden", "");
    a.setAttribute("href", url);
    a.setAttribute("download", `Exceptions_Master_Sheet_${new Date().toISOString().split("T")[0]}.csv`);
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  return (
    <div className="max-w-7xl mx-auto space-y-6 fade-up">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-display text-stone-900">Exceptions Report</h1>
          <p className="text-stone-500 mt-1">View and download students with Back Papers or Grace Marks.</p>
        </div>
        <Button onClick={downloadCSV} disabled={data.length === 0} className="rounded-sm">
          <Download className="w-4 h-4 mr-2" /> Export to CSV
        </Button>
      </div>

      <Card className="p-6 bg-white border-stone-200 shadow-sm rounded-sm">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 items-end">
          <div>
            <label className="block text-xs font-semibold text-stone-600 uppercase mb-2">Program</label>
            <select
              className="w-full p-2 border border-stone-300 rounded-sm text-sm bg-stone-50"
              value={filters.program}
              onChange={e => setFilters({ ...filters, program: e.target.value })}
            >
              <option value="">All Programs</option>
              {meta.programs.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-stone-600 uppercase mb-2">Branch</label>
            <select
              className="w-full p-2 border border-stone-300 rounded-sm text-sm bg-stone-50"
              value={filters.branch}
              onChange={e => setFilters({ ...filters, branch: e.target.value })}
            >
              <option value="">All Branches</option>
              {meta.branches.map(b => <option key={b} value={b}>{b}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-stone-600 uppercase mb-2">Batch</label>
            <select
              className="w-full p-2 border border-stone-300 rounded-sm text-sm bg-stone-50"
              value={filters.batch}
              onChange={e => setFilters({ ...filters, batch: e.target.value })}
            >
              <option value="">All Batches</option>
              {meta.batches.map(b => <option key={b} value={b}>{b}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-stone-600 uppercase mb-2">Semester</label>
            <select
              className="w-full p-2 border border-stone-300 rounded-sm text-sm bg-stone-50"
              value={filters.semester}
              onChange={e => setFilters({ ...filters, semester: e.target.value })}
            >
              <option value="">All Semesters</option>
              {meta.semesters.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <Button onClick={fetchReport} className="w-full rounded-sm" disabled={loading}>
            <Search className="w-4 h-4 mr-2" /> {loading ? "Searching..." : "Search"}
          </Button>
        </div>
      </Card>

      <Card className="bg-white border-stone-200 shadow-sm rounded-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left">
            <thead className="bg-stone-100 text-stone-600 text-xs uppercase font-semibold">
              <tr>
                <th className="px-6 py-4">Roll No</th>
                <th className="px-6 py-4">Name</th>
                <th className="px-6 py-4">Course Details</th>
                <th className="px-6 py-4">Result</th>
                <th className="px-6 py-4 w-1/3">Remark</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-100">
              {data.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center text-stone-500">
                    <AlertCircle className="w-8 h-8 mx-auto text-stone-300 mb-3" />
                    No remarks or exceptions found for these filters.
                  </td>
                </tr>
              ) : (
                data.map((row, idx) => (
                  <tr key={idx} className="hover:bg-stone-50 transition-colors">
                    <td className="px-6 py-4 font-mono font-medium text-stone-900">{row.roll_no}</td>
                    <td className="px-6 py-4 font-medium">{row.name}</td>
                    <td className="px-6 py-4">
                      <div className="text-stone-900">{row.program} ({row.branch})</div>
                      <div className="text-xs text-stone-500 mt-1">Batch {row.batch} • Sem {row.semester}</div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-[11px] font-bold uppercase rounded-sm ${
                        (row.result || "").includes("PASS") ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"
                      }`}>
                        {row.result || "N/A"}
                      </span>
                    </td>
                    <td className="px-6 py-4 font-mono text-xs text-red-600 leading-relaxed">
                      {row.remark}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
